import React, { useState } from 'react';
import { ImSpinner2 } from 'react-icons/im';
import { SortDescriptor } from 'react-stately';

import {
  MeetingsQueryParams,
  useGetVirtualConferenceDashboardMeetings,
} from '../api/virtualConferenceDashboard';
import { VirtualConferenceDashboardFilterBar } from '../components/VirtualConferenceDashboardFilterBar';
import { VirtualConferenceDashboardTable } from '../components/VirtualConferenceDashboardTable';
import { SortDescriptorToApiParam } from '@/utils/sorting';
import { formatDateParam, getYesterday } from '../utils';

const VirtualConferenceDashboardPage = () => {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'MeetingDateTime',
    direction: 'descending',
  });

  const [activeFilters, setActiveFilters] = useState<{
    dateFrom: string;
    dateTo: string;
    organizationId?: string;
  }>({
    dateFrom: formatDateParam(getYesterday()),
    dateTo: formatDateParam(new Date()),
  });

  const queryParams: MeetingsQueryParams = {
    sortOrder: SortDescriptorToApiParam(sortDescriptor),
    ...activeFilters,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching, isError } =
    useGetVirtualConferenceDashboardMeetings(queryParams);

  const handleRun = (filters: { dateFrom: string; dateTo: string; organizationId?: string }) => {
    setActiveFilters(filters);
  };

  if (isError) {
    return (
      <div className="font-lato mt-12 px-8">
        <p className="text-red-600" data-cy="vcd-error-message">
          Something went wrong loading the dashboard. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="font-lato">
      <div className="flex flex-col justify-center my-3">
        <div className="mt-12 px-8 flex justify-between items-center">
          <div className="flex text-zinc-800 items-center">
            <h1 className="text-2xl font-semibold" data-cy="vcd-page-title">
              Virtual Conference Dashboard
            </h1>
          </div>
        </div>
        <div className="w-full px-8 pb-20">
          <VirtualConferenceDashboardFilterBar onRun={handleRun} isLoading={isFetching} />
          <div className="bg-white shadow-sm px-2 border rounded-lg w-full mt-6 relative">
            {isLoading ? (
              <div
                className="flex flex-col items-center justify-center py-20 text-zinc-500"
                data-cy="vcd-loading">
                <ImSpinner2 className="w-8 h-8 animate-spin mb-3" />
                <span className="text-sm">Loading meetings...</span>
              </div>
            ) : (
              <>
                {isFetching && !isFetchingNextPage && (
                  <div
                    className="absolute inset-0 bg-white/60 z-10 flex flex-col items-center justify-center"
                    data-cy="vcd-refetching">
                    <ImSpinner2 className="w-8 h-8 text-zinc-500 animate-spin mb-3" />
                    <span className="text-sm text-zinc-500">Updating results...</span>
                  </div>
                )}
                <VirtualConferenceDashboardTable
                  data={data}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  sortDescriptor={sortDescriptor}
                  onSortChange={setSortDescriptor}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualConferenceDashboardPage;
